'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Profile from '@/assets/icons/common/avatar.svg';
import { domain_url, FB_LINK, INSTA_LINK, LINKEDIN_LINK, TWITTER_LINK, YT_LINK } from '@/constants/apiConstant';
import { createAuthorSlugUrl } from '@/constants/queryParams/authorQueryParams';
import NewsLayouts from '@/layouts/Newslayouts';
import RootLayouts from '@/layouts/Rootlayouts';
import { newsBlogFormatDate } from '@/utils/formatDate';
import sanitizeHTML from '@/utils/sanatizeHtml';
import { generateArticleSchema, generateBreadcrumbSchema, generateWebPageSchema } from '@/utils/seo/schema';
import * as cheerio from 'cheerio';

import Sidebar from '@/components/blogs/Sidebar';
import AuthorBio from '@/components/common/AuthorBio';
import Breadcrumb from '@/components/common/seo/BreadCrumbs';
import Separator from '@/components/common/Separator';
import LiveNewsUpdates from '@/components/news/LiveNewsComponent';
import NewsSidebar from '@/components/news/NewsRightSidebar';
import RelatedNewsCard from '@/components/news/RelatedNewsCard';

const processNewsContent = (html: string, featuredImage?: { url: string; name?: string }) => {
  const $ = cheerio.load(html);

  // Style all links in the content to be blue
  $('a').css({
    color: '#2563eb', // Blue color (Tailwind's blue-600)
    'text-decoration': 'underline',
    cursor: 'pointer',
  });

  // Add hover effect using classes
  $('a').addClass('hover:text-blue-800');

  // Insert a properly-sized image at the beginning of the content with better styling - positioned on LEFT side
  if (featuredImage?.url) {
    // Find the first paragraph or element to insert before
    const firstElement = $('p, h1, h2, h3, h4, h5, h6').first();

    if (firstElement.length) {
      firstElement.before(`
        <div style="float: left; margin-right: 24px;  max-width: 450px; width: 100%; margin-top: 5px;">
          <img 
            src="${featuredImage.url}" 
            alt="${featuredImage.name ?? 'News Featured Image'}"
            width="450" 
            height="300"
            style="border-radius: 8px; width: 100%; height: auto; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);" 
          />
        </div>
      `);
    }
  }

  return $('body').html() ?? html;
};

const NewsLandingPage = ({ newsData, newsId, relatedNews, updatedNews }: any) => {
  const news_url = newsId && newsData && `${domain_url}/news/${newsData?.url}`;
  const title = newsData?.name;

  const breadcrumbItems = generateBreadcrumbSchema(title, news_url, domain_url, 'news', 'News');

  const cleanedHTML = sanitizeHTML(newsData?.content ?? '');
  const finalHTML = processNewsContent(cleanedHTML, newsData?.featured_image);
  const author_name = newsData?.data?.updated_by_user?.name ?? newsData?.created_by_user?.name ?? newsData?.author_name;

  const webPageSchema = generateWebPageSchema(title, news_url);

  const latestLiveUpdateTime = updatedNews?.live_updates?.reduce((latest: string, update: any) => {
    return new Date(update?.updated_time) > new Date(latest) ? update?.updated_time : latest;
  }, newsData?.updatedAt);

  const dateModified =
    new Date(latestLiveUpdateTime) > new Date(newsData?.updatedAt) ? latestLiveUpdateTime : newsData?.updatedAt;

  // Fixed authorData structure to match what AuthorBio component expects
  const hasValidUser = (user: any) => {
    return user && (user.id || user.user_id || user._id);
  };

  const authorData = {
    updated_by_user: hasValidUser(newsData?.updated_by_user)
      ? {
          ...newsData.updated_by_user,
          id: newsData.updated_by_user.id ?? newsData.updated_by_user.user_id ?? newsData.updated_by_user._id,
          name: newsData.updated_by_user.name ?? 'Tanny Verma', // Static fallback
        }
      : null,

    created_by_user: hasValidUser(newsData?.created_by_user)
      ? {
          ...newsData.created_by_user,
          id: newsData.created_by_user.id ?? newsData.created_by_user.user_id ?? newsData.created_by_user._id,
          name: newsData.created_by_user.name ?? 'Tanny Verma', // Static fallback
        }
      : !hasValidUser(newsData?.updated_by_user) && !hasValidUser(newsData?.created_by_user)
        ? {
            name: newsData?.author_name ?? 'Tanny Verma',
            profile_picture: newsData?.featured_image ?? null,
            designation: 'Content Writer',
            author_bio: 'Experienced content writer and journalist.',
            id: newsData?.authorId ?? null,
          }
        : null,
  };

  const articleSchema = generateArticleSchema(
    title,
    domain_url,
    news_url,
    author_name,
    dateModified,
    newsData?.publishedAt,
    newsData?.featured_image?.url,
  );

  const liveBlogSchema = {
    '@context': 'https://schema.org',
    '@type': 'LiveBlogPosting',
    headline: title,
    url: news_url,
    datePublished: newsData?.publishedAt,
    dateModified: dateModified,
    coverageStartTime: updatedNews?.start_date,
    coverageEndTime: updatedNews?.end_date,
    liveBlogUpdate: updatedNews?.live_updates?.map((update: any) => ({
      '@type': 'BlogPosting',
      headline: update?.heading,
      articleBody: update?.description,
      datePublished: update?.updated_time,
    })),
  };

  const newsArticleSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    image: [newsData?.featured_image?.url],
    datePublished: newsData?.publishedAt,
    dateModified: dateModified,
    author: {
      '@type': 'Person',
      name: author_name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CollegeHai',
      logo: {
        '@type': 'ImageObject',
        url: `${domain_url}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': news_url,
    },
  };

  const newsMediaOrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    name: 'CollegeHai',
    url: domain_url,
    foundingDate: '2024',
    legalName: 'CollegeHai',
    logo: {
      '@type': 'ImageObject',
      url: `${domain_url}/logo.png`,
      inLanguage: 'en',
      caption: 'CollegeHai',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'D-19, UF, Sector 3, Noida Sector 16',
      addressLocality: 'Noida',
      addressRegion: 'Uttar Pradesh',
      addressCountry: 'India',
      postalCode: '201301',
    },
    // contactPoint: {
    //   '@type': 'ContactPoint',
    //   telephone: '+91-XXXXXXXXXX',
    //   areaServed: 'IN',
    //   availableLanguage: 'English',
    // },
    sameAs: [FB_LINK, INSTA_LINK, YT_LINK, TWITTER_LINK, LINKEDIN_LINK],
  };

  return (
    <>
      {domain_url === 'https://collegehai.com' && news_url && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      )}
      {breadcrumbItems.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: breadcrumbItems,
            }),
          }}
        />
      )}
      {articleSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      )}

      {news_url && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(newsArticleSchema) }} />
      )}

      {news_url && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(newsMediaOrganizationSchema) }}
        />
      )}

      {updatedNews && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(liveBlogSchema),
          }}
        />
      )}

      <RootLayouts>
        <div className="mx-auto mb-4 mt-24 w-full  lg:max-w-[73.75rem] 2xl:max-w-screen-xl ">
          <Breadcrumb items={breadcrumbItems} color="text-black" />
        </div>
        <NewsLayouts>
          <main>
            <section className="mx-auto mt-3 max-w-screen-xl p-2 lg:mt-5">
              <div className="flex flex-col gap-8 md:flex-row">
                <div className="flex h-full flex-1 flex-col gap-7 overflow-y-auto">
                  <div className="hide-scrollbar flex h-full flex-col gap-1">
                    <div className="space-y-1">
                      {updatedNews && (
                        <div className="mb-2 flex w-fit items-center gap-1 gap-x-2 rounded-full border border-red-400 px-3 py-0.5 text-base text-red-500">
                          <span className="size-2 rounded-full bg-red-500" />
                          {''}
                          Live
                        </div>
                      )}

                      <h1 className="mb-3 font-times text-2xl font-medium leading-6 tracking-tight">{title}</h1>
                      <span className="flex items-center gap-x-2 text-xs">
                        {newsBlogFormatDate(newsData?.updatedAt)}
                      </span>
                      <div className="flex-col items-start gap-2 text-sm text-black sm:gap-x-4">
                        <div className="flex w-auto flex-col gap-1">
                          <div className="flex items-center gap-2 ">
                            <p className="text-xs text-gray-600">
                              {newsData?.updated_by_user ? 'Updated By -' : 'Created By -'}
                            </p>
                            <Image
                              src={
                                newsData?.updated_by_user?.profile_picture?.url ??
                                newsData?.created_by_user?.profile_picture?.url ??
                                Profile
                              }
                              alt={
                                newsData?.updated_by_user?.name ??
                                newsData?.created_by_user?.name ??
                                newsData?.author_name ??
                                'Tanya Verma'
                              }
                              width={20}
                              height={20}
                              className="size-5 rounded-full"
                            />
                            {newsData?.updated_by_user?.id || newsData?.created_by_user?.id ? (
                              <Link
                                href={
                                  createAuthorSlugUrl(
                                    newsData?.updated_by_user?.name ?? newsData?.created_by_user?.name,
                                    newsData?.updated_by_user?.id ?? newsData?.created_by_user?.id,
                                  ) ?? '#'
                                }
                                className="truncate whitespace-nowrap text-xs text-blue-600 hover:underline"
                              >
                                {newsData?.updated_by_user?.name ??
                                  newsData?.created_by_user?.name ??
                                  newsData?.author_name ??
                                  'Tanny Verma'}
                              </Link>
                            ) : (
                              <span className="truncate whitespace-nowrap text-xs text-gray-800">
                                {newsData?.updated_by_user?.name ??
                                  newsData?.created_by_user?.name ??
                                  newsData?.author_name ??
                                  'Tanny Verma'}
                              </span>
                            )}
                          </div>

                          {/* Designation Section */}
                          {(newsData?.updated_by_user?.designation ?? newsData?.created_by_user?.designation) && (
                            <p className="line-clamp-1 text-xs text-gray-500">
                              <span className="font-medium text-gray-700">Designation - </span>
                              {newsData?.updated_by_user?.designation ?? newsData?.created_by_user?.designation}
                            </p>
                          )}

                          {/* Author Bio Section */}
                          {(newsData?.updated_by_user?.author_bio ?? newsData?.created_by_user?.author_bio) && (
                            <p className="text-xs text-gray-500">
                              <span className="font-medium text-gray-700">Author Bio - </span>
                              {newsData?.updated_by_user?.author_bio ?? newsData?.created_by_user?.author_bio}
                            </p>
                          )}
                        </div>

                        {/* Tags Section */}
                        {newsData?.tags?.length > 0 && (
                          <>
                            <div className="hidden h-5 w-[0.8px] bg-gray-400"></div>
                            <div className="mt-2 flex items-center gap-2">
                              <p className="text-gray-600 ">Tags:</p>
                              <div className="flex gap-2">
                                {newsData?.tags?.map((tag: any) => (
                                  <p
                                    key={tag?.id}
                                    className="rounded-full bg-tertiary px-4 py-1 text-xs font-medium text-white"
                                  >
                                    {tag?.tags_name}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div
                      dangerouslySetInnerHTML={{ __html: finalHTML }}
                      className={`ckeditorData mt-2 w-full min-w-fit p-4`}
                    />
                  </div>
                </div>
                <div className="sticky top-24 h-fit">
                  <NewsSidebar title="Follow us" />
                </div>
              </div>
              <div className="my-4">
                <Separator />
              </div>

              {/* Update news */}

              {updatedNews && <LiveNewsUpdates newsId={newsId} initialUpdates={updatedNews} />}

              <Sidebar title={title} url={news_url} />

              {relatedNews?.length > 0 && (
                <div className="mt-5 flex flex-col gap-4">
                  <h3 className="font-times text-2xl font-medium underline decoration-1 underline-offset-4">
                    Related News
                  </h3>
                  <div className="mb-2 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-6 xl:grid-cols-5">
                    {relatedNews?.slice(0, 5)?.map((latestData: any) => {
                      return <RelatedNewsCard key={latestData.id} latestData={latestData} />;
                    })}
                  </div>
                </div>
              )}
              <AuthorBio userData={authorData} />
            </section>
          </main>
        </NewsLayouts>
      </RootLayouts>
    </>
  );
};

export default NewsLandingPage;
